
import { useEffect, useMemo, useState } from "react";
import api from "../api/axios";
import toast from "react-hot-toast";

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const run = async () => {
      try {
        const [statsRes, txRes, notifRes] = await Promise.all([
          api.get("/admin/analytics/stats"),
          api.get("/admin/transactions"),
          api.get("/admin/notifications/unread"),
        ]);

        setStats(statsRes.data || null);
        setTransactions(Array.isArray(txRes.data) ? txRes.data.filter((txn) => !txn.type?.startsWith("crypto_")) : []);
        setNotifications(Array.isArray(notifRes.data) ? notifRes.data : []);
      } catch (error) {
        toast.error(error.response?.data?.message || "Failed to load admin dashboard");
      } finally {
        setLoading(false);
      }
    };

    run();
  }, []);

  const recentTx = useMemo(() => transactions.slice(0, 6), [transactions]);

  return (
    <div className="mx-auto max-w-7xl space-y-6 text-white">
      <section className="rounded-[2rem] border border-white/10 bg-slate-900/75 p-6 backdrop-blur-xl">
        <p className="text-sm uppercase tracking-[0.24em] text-cyan-300/80">Admin</p>
        <h1 className="mt-2 text-3xl font-bold text-white">Platform Dashboard</h1>
        <p className="mt-2 text-sm text-slate-400">
          Monitor activity, approvals, wallet float, transactions, and unread platform notifications.
        </p>
      </section>

      <section className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
        <div className="rounded-[1.75rem] border border-white/10 bg-slate-900/75 p-5 backdrop-blur-xl">
          <p className="text-xs uppercase tracking-[0.2em] text-slate-400">Total Users</p>
          <p className="mt-3 text-3xl font-bold text-white">{loading ? "..." : stats?.users?.total || 0}</p>
        </div>
        <div className="rounded-[1.75rem] border border-white/10 bg-slate-900/75 p-5 backdrop-blur-xl">
          <p className="text-xs uppercase tracking-[0.2em] text-slate-400">Pending Withdrawals</p>
          <p className="mt-3 text-3xl font-bold text-rose-300">
            {loading ? "..." : stats?.approvals?.bank || 0}
          </p>
        </div>
        <div className="rounded-[1.75rem] border border-white/10 bg-slate-900/75 p-5 backdrop-blur-xl">
          <p className="text-xs uppercase tracking-[0.2em] text-slate-400">Total Transactions</p>
          <p className="mt-3 text-3xl font-bold text-emerald-300">{loading ? "..." : stats?.transactions?.total || 0}</p>
        </div>
        <div className="rounded-[1.75rem] border border-white/10 bg-slate-900/75 p-5 backdrop-blur-xl">
          <p className="text-xs uppercase tracking-[0.2em] text-slate-400">Wallet Float</p>
          <p className="mt-3 text-3xl font-bold text-cyan-300">
            ₦{loading ? "..." : (stats?.wallets?.totalBalance || 0).toLocaleString()}
          </p>
        </div>
      </section>

      <section className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <div className="rounded-[1.75rem] border border-white/10 bg-slate-900/75 p-5 backdrop-blur-xl">
          <p className="text-xs uppercase tracking-[0.2em] text-slate-400">Active Users</p>
          <p className="mt-3 text-2xl font-bold text-white">{loading ? "..." : stats?.users?.active || 0}</p>
        </div>
        <div className="rounded-[1.75rem] border border-white/10 bg-slate-900/75 p-5 backdrop-blur-xl">
          <p className="text-xs uppercase tracking-[0.2em] text-slate-400">Suspended Users</p>
          <p className="mt-3 text-2xl font-bold text-white">{loading ? "..." : stats?.users?.suspended || 0}</p>
        </div>
        <div className="rounded-[1.75rem] border border-white/10 bg-slate-900/75 p-5 backdrop-blur-xl">
          <p className="text-xs uppercase tracking-[0.2em] text-slate-400">Unread Notifications</p>
          <p className="mt-3 text-2xl font-bold text-white">{loading ? "..." : notifications.length}</p>
        </div>
      </section>

      <section className="rounded-[2rem] border border-white/10 bg-slate-900/75 p-4 md:p-6 backdrop-blur-xl">
        <div className="mb-4 flex items-center justify-between gap-4">
          <div>
            <h2 className="text-lg font-semibold text-white">Recent Transactions</h2>
            <p className="text-sm text-slate-400">Latest platform transaction activity.</p>
          </div>
        </div>

        {!loading && recentTx.length === 0 ? (
          <div className="rounded-xl border border-white/10 bg-white/5 p-6 text-center text-slate-400">
            No transactions yet.
          </div>
        ) : (
          <>
            <div className="hidden overflow-x-auto md:block">
              <table className="min-w-full border-separate border-spacing-y-2 text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wider text-slate-400">
                    <th className="px-4 py-2">User</th>
                    <th className="px-4 py-2">Type</th>
                    <th className="px-4 py-2">Amount</th>
                    <th className="px-4 py-2">Status</th>
                    <th className="px-4 py-2">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {recentTx.map((tx) => (
                    <tr key={tx._id} className="bg-white/5 text-slate-200">
                      <td className="rounded-l-2xl px-4 py-4 text-white">{tx.wallet?.user?.fullName || "N/A"}</td>
                      <td className="px-4 py-4 capitalize text-slate-300">{tx.type.replace("_", " ")}</td>
                      <td className="px-4 py-4 font-semibold text-white">₦{tx.amount.toLocaleString()}</td>
                      <td className="px-4 py-4">
                        <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${tx.status === "success" ? "bg-emerald-500/15 text-emerald-300" : tx.status === "pending" ? "bg-amber-500/15 text-amber-300" : "bg-rose-500/15 text-rose-300"}`}>
                          {tx.status}
                        </span>
                      </td>
                      <td className="rounded-r-2xl px-4 py-4 text-slate-400">{new Date(tx.createdAt).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="space-y-3 md:hidden">
              {recentTx.map((tx) => (
                <div key={tx._id} className="rounded-2xl border border-white/10 bg-white/5 p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="font-semibold text-white">{tx.wallet?.user?.fullName || "N/A"}</p>
                      <p className="text-sm capitalize text-slate-300">{tx.type.replace("_", " ")}</p>
                    </div>
                    <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${tx.status === "success" ? "bg-emerald-500/15 text-emerald-300" : tx.status === "pending" ? "bg-amber-500/15 text-amber-300" : "bg-rose-500/15 text-rose-300"}`}>
                      {tx.status}
                    </span>
                  </div>
                  <p className="mt-3 text-lg font-bold text-white">₦{tx.amount.toLocaleString()}</p>
                  <p className="mt-1 text-xs text-slate-400">{new Date(tx.createdAt).toLocaleString()}</p>
                </div>
              ))}
            </div>
          </>
        )}
      </section>

      <section className="rounded-[2rem] border border-white/10 bg-slate-900/75 p-6 backdrop-blur-xl">
        <h2 className="text-lg font-semibold text-white">Unread Notifications</h2>
        <p className="mt-1 text-sm text-slate-400">Latest platform alerts requiring admin attention.</p>
        <ul className="mt-5 space-y-3">
          {!loading && notifications.length === 0 && (
            <li className="rounded-xl border border-white/10 bg-white/5 p-4 text-sm text-slate-400">
              No unread notifications.
            </li>
          )}
          {notifications.slice(0, 6).map((n) => (
            <li key={n._id} className="flex flex-col gap-3 rounded-xl border border-white/10 bg-white/5 p-4 md:flex-row md:items-center md:justify-between">
              <div>
                <p className="text-sm text-white">{n.message}</p>
                <p className="mt-1 text-xs text-slate-500">{new Date(n.createdAt).toLocaleString()}</p>
              </div>
              <span className="w-fit rounded-full bg-amber-500/15 px-3 py-1 text-xs font-semibold capitalize text-amber-300">
                {n.type}
              </span>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}
